import api from "@/config/axiosConfig";
import {
  Profile,
  ProfileImage,
  BackgroundImage,
  User,
  SimilarUser,
} from "@/types/user";
import { AxiosResponse } from "axios";

interface UpdateProfileData {
  username?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  is_private?: boolean;
}

class UserService {
  async getUserProfile(
    userId: string,
    includeSimilarUsers: boolean = false
  ): Promise<{
    user: Profile;
    similar_users: SimilarUser[];
  }> {
    const response = await api.social.get(
      `/users/${userId}${includeSimilarUsers ? "?include_similar=true" : ""}`
    );
    return {
      user: response.data.user,
      similar_users: response.data.similar_users,
    };
  }

  async updateProfileImage(file: File): Promise<ProfileImage> {
    const formData = new FormData();
    formData.append("profile_image", file);
    const response: AxiosResponse<ProfileImage> = await api.auth.put(
      "/users/profile-image",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async updateBackgroundImage(file: File): Promise<BackgroundImage> {
    const formData = new FormData();
    formData.append("background_image", file);
    const response: AxiosResponse<BackgroundImage> = await api.auth.put(
      "/users/background-image",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async updateBackgroundImageUrl(imageUrl: string): Promise<BackgroundImage> {
    const response: AxiosResponse<BackgroundImage> = await api.auth.put(
      "/users/background-image",
      { background_image: imageUrl }
    );
    return response.data;
  }

  async updateUserPublicity(isPublic: boolean): Promise<{
    success: boolean;
    is_private: boolean;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      is_private: boolean;
    }> = await api.auth.put("/users/publicity", { is_public: isPublic });
    return response.data;
  }

  // follow user
  async followUser(userId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      message: string;
    }> = await api.social.post("/follow", {
      user_id: userId,
    });
    return response.data;
  }

  // unfollow user
  async unfollowUser(userId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      message: string;
    }> = await api.social.post("/unfollow", {
      user_id: userId,
    });
    return response.data;
  }

  async approveFollowRequest(
    userId: string,
    action: "approve" | "decline"
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      message: string;
    }> = await api.social.post("/handle-follow-request", {
      user_id: userId,
      action: action,
    });
    return response.data;
  }

  async getFriends(userId: string): Promise<
    {
      user_id: string;
      name: string;
      profile_image: string;
    }[]
  > {
    const response: AxiosResponse<{
      friends: { user_id: string; name: string; profile_image: string }[];
    }> = await api.social.get("/friends");
    return response.data.friends;
  }

  async updateProfile(data: UpdateProfileData) {
    const response = await api.auth.put("/users/profile", data);
    return response.data;
  }

  async deleteAccount() {
    const response = await api.auth.delete("/users");
    return response.data;
  }
}

export const userService = new UserService();
export default userService;
